/*
 * Application area (audit Phase 3.3). Replaces the seven legacy
 * wwwroot/js/Application/*.js files -- List, Create, Detail, Resource, Role,
 * Person and Profile -- with one module on the Phase 3.1 core. No jQuery.
 *
 * Shape of the screen: Application/ListByDomain renders a picker of
 * applications beside a detail pane; selecting one swaps Application/Detail
 * into #ApplicationDetailContainer, and that view's lazy tabs (People, Roles,
 * Resources, Features, ...) each load a partial from their own controller.
 * Every handler below is reached by data-action from one of those partials.
 *
 * Registered eagerly in main.ts, and the registry is global, so no window
 * mirror is needed -- the inline onchange= handlers the old views used were
 * converted to data-action / delegated listeners as part of this move. The
 * ApplicationRole partials this area drives are shared with /Role (see
 * areas/role.ts), which reaches Application.Role.* through the same registry.
 */
import { ajaxHtml, ajaxJson, ajaxModal, ajaxSwap } from '../core/ajax';
import { all, el, escapeHtml, one, swapHtml } from '../core/dom';
import { serialize } from '../core/forms';
import { modelStateSummary, type JsonEnvelope } from '../core/http';
import { confirm, hideModal, showModalElement } from '../core/modal';
import { initTabs, showTab } from '../core/tabs';
import { toast } from '../core/toast';
import { setValidationModelState, isValid } from '../core/validation';
import { friendlyVerbList, subtractVerbs, unionVerbs } from './application-verbs';

/** The ClaimValueType every REST-action permission carries. */
const RESTACTION = 'mcp.stonaltar.com/restaction';

// ---------------------------------------------------------------- helpers

function value(id: string): string {
    const field = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
    return field?.value ?? '';
}

function valueIn(root: ParentNode | null, selector: string): string {
    const field = root?.querySelector(selector) as HTMLInputElement | HTMLSelectElement | null;
    return field?.value ?? '';
}

function checkedIn(root: ParentNode | null, selector: string): boolean {
    return (root?.querySelector(selector) as HTMLInputElement | null)?.checked === true;
}

/** The application the open detail pane / modal belongs to. */
function applicationId(): string {
    return value('ApplicationId');
}

function currentDomainId(): string {
    return value('CurrentDomainId');
}

function summaryOf(data: unknown): string {
    const modelState = (data as { modelState?: unknown } | null)?.modelState;
    return modelState ? modelStateSummary(modelState) : '';
}

/** Un-hide / hide by d-none across every match, the pattern the row spinners use. */
function setHidden(selector: string, hidden: boolean): void {
    for (const node of all(selector)) node.classList.toggle('d-none', hidden);
}

/** Legacy rows toggle visibility with inline display rather than d-none. */
function setDisplay(id: string, shown: boolean): void {
    const node = document.getElementById(id);
    if (node) node.style.display = shown ? '' : 'none';
}

function remove(selector: string): void {
    for (const node of all(selector)) node.remove();
}

// ---------------------------------------------------------------- List

export const List = {
    /** Show one application's detail pane and open its People tab. */
    Select(id: number | string): void {
        void ajaxSwap({
            container: 'ApplicationDetailContainer',
            url: `Application/${id}`,
            spinnerInto: true,
            alertTitle: 'Detail Action failed to execute',
            onSuccess: () => {
                for (const tile of all('.mcp-picker-item')) tile.classList.remove('selected');
                one(`.mcp-picker-item[data-id="${id}"]`)?.classList.add('selected');
                initTabs();
                showTab('#ApplicationPeopleLink');
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Detail Action failed to execute', error);
                return true;
            },
        });
    },

    /** Swap the New Application form into the detail pane. */
    Create(): void {
        void ajaxSwap({
            container: 'ApplicationDetailContainer',
            url: `Application/Create?domainId=${encodeURIComponent(currentDomainId())}`,
            spinnerInto: true,
            onSuccess: () => {
                for (const tile of all('.mcp-picker-item')) tile.classList.remove('selected');
                (one('#CreateApplicationForm #Name') as HTMLInputElement | null)?.focus();
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'New Application', `New Application View failed to execute: ${error}`);
                return true;
            },
        });
    },

    /** Re-render the whole screen, optionally selecting an application afterwards. */
    Reload(selectId?: number | string): void {
        const wanted = Number.parseInt(String(selectId ?? ''), 10);
        void ajaxSwap({
            container: 'ViewContainer',
            url: `Application/Domain/${currentDomainId()}`,
            onSuccess: () => {
                if (Number.isFinite(wanted) && wanted > 0) {
                    const tile = one(`#ApplicationListContainer .mcp-picker-item[data-id="${wanted}"]`);
                    if (tile) {
                        for (const other of all('#ApplicationListContainer .mcp-picker-item')) other.classList.remove('selected');
                        tile.classList.add('selected');
                        tile.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                    List.Select(wanted);
                } else {
                    initTabs();
                    showTab('#ApplicationPeopleLink');
                }
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'An error occurred loading applications', error);
                return true;
            },
        });
    },
};

// ---------------------------------------------------------------- Create

/**
 * Live preview of the application's full URL as the operator types a prefix.
 * Delegated rather than an inline onchange= so it survives every AJAX swap
 * and is scanned by the view-wiring tests along with the data-actions.
 */
function bindUrlPreview(): void {
    const update = (input: HTMLInputElement): void => {
        const form = input.closest('form') ?? document;
        const domainUrl = (form.querySelector('#DomainUrl') as HTMLElement | null)?.textContent?.trim() ?? '';
        const preview = document.getElementById('ApplicationUrl');
        if (!preview) return;
        const prefix = input.value.trim();
        preview.textContent = prefix && domainUrl ? `${prefix}.${domainUrl}` : (prefix || domainUrl);
    };
    document.addEventListener('input', event => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches('[data-app-url-prefix]')) update(target);
    });
}

/**
 * Live preview of a child resource's URI on the ApplicationResource entry
 * forms. Mirrors McpResourceUri.ComposeChild and nothing else: the segment is
 * the prefix, or the name when there is no prefix, appended to the parent
 * application's URI -- which the server already composed into #ApplicationUrl,
 * so this cannot become a second spelling of the rule.
 *
 * The forms previously called Resource.Create.DomainPrefixChange inline, a
 * legacy global whose own rule read #ResourceAppId -- an id no rendered page
 * has ever contained -- so it wrote an empty string every time and the URI
 * preview on the live screen was permanently blank.
 */
function resourceUriPreview(root: ParentNode): void {
    const form = (root instanceof Element && root.closest('form')) || root;
    const preview = (form.querySelector('#ResourceUrl') ?? document.getElementById('ResourceUrl')) as HTMLElement | null;
    if (!preview) return;
    const parentUrl = (form.querySelector('#ApplicationUrl') as HTMLElement | null)?.textContent?.trim() ?? '';
    const prefix = (form.querySelector('[data-resource-url-prefix]') as HTMLInputElement | null)?.value.trim() ?? '';
    const name = (form.querySelector('#Resource_Name') as HTMLInputElement | null)?.value.trim() ?? '';
    const segment = prefix || name;
    preview.textContent = parentUrl ? `${parentUrl}/${segment}` : segment;
}

function bindResourceUriPreview(): void {
    document.addEventListener('input', event => {
        const target = event.target;
        if (target instanceof HTMLInputElement && target.matches('[data-resource-url-prefix]')) {
            resourceUriPreview(target);
        }
    });
}

/** Seeds the preview when an ApplicationResource form lands; an enhancer. */
export function enhanceResourceUriPreview(root: ParentNode): void {
    const input = root instanceof Element && root.matches('[data-resource-url-prefix]')
        ? root
        : root.querySelector('[data-resource-url-prefix]');
    if (input) resourceUriPreview(input);
}

export const Create = {
    /** POST the new application, then reload the list with it selected. */
    Save(): void {
        const form = one('CreateApplicationForm') as HTMLFormElement | null;
        if (!form) return;
        const data = serialize(form);
        data.push({ name: 'DomainId', value: currentDomainId() });

        setHidden('#ApplicationCreateContainer .fa-save', true);
        setHidden('#ApplicationCreateContainer .fa-spinner', false);
        void ajaxJson({
            url: 'Application',
            method: 'POST',
            data,
            onSuccess: (resp: JsonEnvelope) => {
                // The controller answers { success, applicationId } -- camelCase,
                // like every Web Json(); the legacy caller read data.ApplicationId
                // and always got undefined, so a new application was never selected.
                List.Reload(resp['applicationId'] as number | undefined);
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState);
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to create the application', error);
                return true;
            },
            onComplete: () => {
                setHidden('#ApplicationCreateContainer .fa-save', false);
                setHidden('#ApplicationCreateContainer .fa-spinner', true);
            },
        });
    },

    /** Abandon the form and go back to the first application in the list. */
    Cancel(): void {
        const first = one('#ApplicationListContainer .mcp-picker-item');
        List.Reload(first?.getAttribute('data-id') ?? 0);
    },
};

// ---------------------------------------------------------------- Detail

interface MigrationResult {
    tenantCreated?: boolean;
    tenantName?: string;
    assignedCount?: number;
    alreadyAssignedCount?: number;
}

export const Detail = {
    EditView(appId: number | string): void {
        void ajaxSwap({
            container: 'ApplicationDetailContainer',
            url: `Application/${appId}/Edit`,
            alertTitle: 'Edit Application failed to execute',
        });
    },

    EditSave(appId: number | string): void {
        const form = one('EditApplicationForm') as HTMLFormElement | null;
        if (!form || !isValid(form)) return;

        // Flipping TenancyMode None -> MultiTenant through the plain PUT is
        // refused by the API with a 400: the operator has to say what happens
        // to the existing users. data-original carries the in-DB value at
        // render time (see Application/Edit.cshtml), so the transition is
        // detectable here and shunted to the migration modal instead.
        const tenancy = document.getElementById('TenancyMode') as HTMLSelectElement | null;
        const selected = Number.parseInt(tenancy?.value ?? '', 10);
        const original = Number.parseInt(tenancy?.getAttribute('data-original') ?? '', 10);
        if (selected === 1 && original === 0) {
            Detail.TenancyMigrationOpen(appId);
            return;
        }

        // The buttons class is application-edit-buttons; the legacy handler
        // spelled it "applicatoin-", so nothing was ever disabled and the
        // save spinner never appeared.
        const buttons = all('.application-edit-buttons .btn');
        for (const button of buttons) button.setAttribute('disabled', 'disabled');
        setHidden('.application-edit-buttons .fa-save', true);
        setHidden('.application-edit-buttons .fa-spinner', false);

        void ajaxSwap({
            container: 'ApplicationDetailContainer',
            url: `Application/${appId}`,
            method: 'PUT',
            data: serialize(form),
            alertTitle: 'Edit Application failed to save',
            onSuccess: () => {
                initTabs();
                showTab('#ApplicationPeopleLink');
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Edit Application failed to save', error);
                return true;
            },
            onComplete: () => {
                for (const button of buttons) button.removeAttribute('disabled');
                setHidden('.application-edit-buttons .fa-save', false);
                setHidden('.application-edit-buttons .fa-spinner', true);
            },
        });
    },

    TenancyMigrationOpen(appId: number | string): void {
        void ajaxModal({
            modal: 'TenancyMigrationModal',
            url: `Application/${appId}/TenancyMigration`,
            errorTitle: 'Multi-tenant migration',
            errorMessage: 'Failed to render the migration confirmation modal.',
        });
    },

    TenancyMigrationConfirm(): void {
        const form = one('TenancyMigrationForm') as HTMLFormElement | null;
        const appId = value('TenancyMigrationApplicationId');
        setHidden('#TenancyMigrationForm .fa-check', true);
        setHidden('#TenancyMigrationForm .fa-spinner', false);
        void ajaxJson({
            url: `Application/${appId}/TenancyMigration`,
            method: 'POST',
            data: form ? serialize(form) : [],
            alertInto: 'TenancyMigrationMessageContainer',
            errorTitle: 'Migration failed',
            onSuccess: resp => {
                hideModal('TenancyMigrationModal');
                const result = (resp as { result?: MigrationResult }).result;
                const parts: string[] = [];
                if (result?.tenantCreated) parts.push(`created '${result.tenantName ?? ''}'`);
                if ((result?.assignedCount ?? 0) > 0) parts.push(`assigned ${result?.assignedCount} user(s)`);
                if ((result?.alreadyAssignedCount ?? 0) > 0) parts.push(`${result?.alreadyAssignedCount} already in a Tenant (left alone)`);
                toast('success', 'Multi-tenant enabled', parts.length > 0 ? parts.join('; ') : 'Mode flipped');
                List.Select(appId);
            },
            onFailure: data => {
                const modelState = (data as { modelState?: Record<string, string[]> } | null)?.modelState;
                if (modelState) setValidationModelState(modelState);
                toast('danger', 'Migration failed', summaryOf(data));
                return true;
            },
            onComplete: () => {
                setHidden('#TenancyMigrationForm .fa-check', false);
                setHidden('#TenancyMigrationForm .fa-spinner', true);
            },
        });
    },

    Delete(appId: number | string): void {
        const id = Number.parseInt(String(appId), 10);
        if (!Number.isFinite(id) || id <= 0) {
            toast('danger', 'Application delete failed', 'missing application id');
            return;
        }
        void confirm({
            title: 'Delete Application',
            html: 'Delete this Application?<br/><br/>'
                + '<span class="text-danger"><strong>WARNING:</strong> '
                + 'all related resources and privileges will also be deleted.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Application/${id}`,
                    method: 'DELETE',
                    onSuccess: () => {
                        toast('success', 'Application deleted');
                        // The picker still lists it: re-render rather than leave
                        // a tile that now resolves to nothing.
                        List.Reload(0);
                    },
                    onFailure: data => {
                        toast('danger', 'Application delete failed to execute', summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Application delete failed to execute', error);
                        return true;
                    },
                });
            },
        });
    },

    // RoleDetailModal removed in the Phase 3.3 Role migration. Its only caller
    // was the "grid" branch of Views/Role/List.cshtml, unreachable markup (no
    // caller ever passed a viewStyle). It was broken in its own right: it swapped
    // the partial into the row's data-target -- #RoleDetailContainer, the inline
    // detail pane -- and then tried to show #RoleDetailModal, which no page has
    // ever rendered. Role.List.Select owns that click now.
};

// ---------------------------------------------------------------- Resource

export const Resource = {
    GetList(appId: number | string): void {
        void ajaxSwap({
            container: 'ApplicationResourcesTab',
            url: `ApplicationResource/Application/${appId}`,
            busy: '#AppResourcesContainer .fa-spinner',
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to render the resource list', error);
                return true;
            },
        });
    },

    Create(appId: number | string): void {
        Resource._form(`ApplicationResource/Application/${appId}/Create`, 'Failed to render new resource entry-form');
    },

    Edit(resourceId: number | string): void {
        Resource._form(`ApplicationResource/Resource/${resourceId}/Edit`, 'Failed to render resource detail entry-form');
    },

    /** Both entry forms are the same modal swap; only the URL differs. */
    _form(url: string, errorTitle: string): void {
        setHidden('#AppResourcesContainer .fa-plus', true);
        void ajaxSwap({
            container: 'ApplicationDetailModal',
            url,
            busy: '#AppResourcesContainer .fa-spinner',
            onSuccess: () => {
                const modal = one('ApplicationDetailModal');
                if (modal) showModalElement(modal);
            },
            onError: (_xhr, _status, error) => {
                toast('danger', errorTitle, error);
                return true;
            },
            onComplete: () => setHidden('#AppResourcesContainer .fa-plus', false),
        });
    },

    CreateSave(): void {
        Resource._save('ApplicationResource', 'POST', 'Failed to save new resource');
    },

    EditSave(): void {
        Resource._save(`ApplicationResource/${value('Resource_ResourceId')}`, 'PUT', 'Failed to save resource changes');
    },

    _save(url: string, method: string, errorTitle: string): void {
        const appId = applicationId();
        const form = one('CreateResourceForm') as HTMLFormElement | null;
        setHidden('#AppResourcesContainer .fa-save', true);
        void ajaxJson({
            url,
            method,
            data: form ? serialize(form) : [],
            busy: '#AppResourcesContainer .fa-spinner',
            onSuccess: () => {
                hideModal('ApplicationDetailModal');
                Resource.GetList(appId);
            },
            onFailure: data => {
                setValidationModelState((data as { modelState?: Record<string, string[]> } | null)?.modelState, 'ApplicationDetailModal');
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', errorTitle, error);
                return true;
            },
            onComplete: () => setHidden('#AppResourcesContainer .fa-save', false),
        });
    },

    EditCancel(): void {
        hideModal('ApplicationDetailModal');
    },

    Delete(resourceId: number | string): void {
        const rowSelector = `.application-resource-row[data-id='${resourceId}']`;
        void confirm({
            title: 'Delete Resource',
            html: 'Delete this Resource?<br/><br/>'
                + '<span class="text-danger">Application functions may be affected.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                setHidden(`${rowSelector} .fa-trash`, true);
                void ajaxJson({
                    url: `ApplicationResource/${resourceId}`,
                    method: 'DELETE',
                    busy: `${rowSelector} .fa-spinner`,
                    onSuccess: () => remove(rowSelector),
                    onFailure: data => {
                        toast('danger', 'Failed to delete resource', summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to delete resource', error);
                        return true;
                    },
                    onComplete: () => setHidden(`${rowSelector} .fa-trash`, false),
                });
            },
        });
    },

    Detail(resourceId: number | string): void {
        setDisplay(`ApplicationResourceBusyIndicator${resourceId}`, true);
        void ajaxModal({
            modal: 'ApplicationDetailModal',
            url: `Resource/Detail/${resourceId}`,
            errorTitle: 'Resource Detail',
            errorMessage: 'Failed to display resource detail',
            onComplete: () => setDisplay(`ApplicationResourceBusyIndicator${resourceId}`, false),
        });
    },
};

// ---------------------------------------------------------------- Role

/**
 * The role / member / permission "add" endpoints answer with HTML, and the
 * same URL returns either the saved row or the entry form again with
 * validation messages. The row class in the response is what tells them
 * apart -- there is no envelope to read.
 */
async function saveInlineRow(url: string, data: Record<string, unknown>, rowClass: string, errorTitle: string): Promise<void> {
    const row = one(`tr.${rowClass}`);
    if (!row) return;
    setHidden(`tr.${rowClass} .fa-spinner`, false);
    const html = await ajaxHtml({
        url,
        method: 'POST',
        data,
        onError: (xhr, _status, error) => {
            toast('danger', errorTitle, xhr.status === 404 ? 'Not found' : error);
            return true;
        },
        onComplete: () => setHidden(`tr.${rowClass} .fa-spinner`, true),
    });
    if (html === null) return;
    if (html.includes(rowClass)) {
        swapHtml(row, html, 'replaceWith');           // still the form: validation failed
    } else {
        swapHtml(row, html, 'before');
        row.classList.add('d-none');
    }
}

/** Read one inline claim row's fields into the shape the claim endpoints bind. */
function claimRowData(row: ParentNode | null): Record<string, unknown> {
    const selected = valueIn(row, '#Claim_ClaimValue option:checked');
    return {
        roleId: valueIn(row, '#RoleId'),
        resourceId: valueIn(row, '#Claim_ResourceId'),
        claimType: valueIn(row, '#Claim_ClaimType'),
        claimValue: selected || valueIn(row, '#Claim_ClaimValue'),
        claimValueType: valueIn(row, '#Claim_ClaimValueType'),
        isDeny: checkedIn(row, '#Claim_IsDeny'),
        // Present-but-empty binds to null on int? -- that is how "(always)"
        // clears a resource-policy window.
        resourcePolicyId: valueIn(row, '#Claim_ResourcePolicyId'),
    };
}

export const Role = {
    NewRole(): void {
        const rowSelector = 'tr.new-application-role-row';
        setHidden(rowSelector, false);
        void ajaxSwap({
            container: rowSelector,
            url: `ApplicationRole/Create/${applicationId()}`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to initialize the new role entry-form',
        });
    },

    SaveNewRole(): void {
        const row = one('tr.new-application-role-row');
        void saveInlineRow('ApplicationRole', {
            applicationId: valueIn(row, '#ApplicationId'),
            name: valueIn(row, '#Name'),
            description: valueIn(row, '#Description'),
        }, 'new-application-role-row', 'Failed to save new role.');
    },

    CancelNewRole(): void {
        setHidden('tr.new-application-role-row', true);
    },

    RowView(roleId: number | string): void {
        const rowSelector = `tr.application-role-row[data-id='${roleId}']`;
        void ajaxSwap({
            container: rowSelector,
            url: `ApplicationRole/${roleId}`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to render role',
        });
    },

    Detail(roleId: number | string): void {
        const rowSelector = `tr.application-role-row[data-id='${roleId}']`;
        const roleName = one(`${rowSelector} span.role-name`)?.textContent ?? '';
        void ajaxModal({
            modal: 'ApplicationDetailModal',
            url: `ApplicationRole/Detail/${roleId}`,
            busy: `${rowSelector} .fa-spinner`,
            onSuccess: () => {
                initTabs();
                showTab("#RoleDetailContainer button.nav-link[data-tab='People']");
            },
            onError: (_xhr, _status, error) => {
                toast('danger', `Failed to get detail for ${roleName}.`, error);
                return true;
            },
        });
    },

    Delete(roleId: number | string): void {
        const rowSelector = `tr.application-role-row[data-id='${roleId}']`;
        const roleName = one(`${rowSelector} span.role-name`)?.textContent ?? '';
        void confirm({
            title: 'Delete role',
            html: `Delete role <strong>${escapeHtml(roleName)}</strong>?<br/><br/>`
                + '<span class="text-danger"><strong>WARNING:</strong> '
                + 'current members of this role may lose permissions and ability to access applications.</span>',
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `ApplicationRole/${roleId}`,
                    method: 'DELETE',
                    busy: `${rowSelector} .fa-spinner`,
                    onSuccess: () => {
                        remove(rowSelector);
                        toast('success', `Role ${roleName} deleted`);
                    },
                    onFailure: data => {
                        toast('danger', `Failed to delete the role ${roleName}.`, summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', `Failed to delete the role ${roleName}.`, error);
                        return true;
                    },
                });
            },
        });
    },

    NewMember(): void {
        const rowSelector = 'tr.new-role-person-row';
        setHidden(rowSelector, false);
        void ajaxSwap({
            container: rowSelector,
            url: `ApplicationRole/${value('RoleId')}/Person/Add`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to initialize the new role member entry-form',
        });
    },

    SaveNewMember(): void {
        const row = one('tr.new-role-person-row');
        void saveInlineRow('ApplicationRole/Person', {
            roleId: valueIn(row, '#RoleId'),
            personId: valueIn(row, '#PersonId'),
        }, 'new-role-person-row', 'Failed to save new role member.');
    },

    CancelNewMember(): void {
        setHidden('tr.new-role-person-row', true);
    },

    RemoveMember(roleId: number | string, personId: number | string): void {
        const rowSelector = `tr.application-role-person-row[data-id='${personId}']`;
        const name = valueIn(one(rowSelector), '#PersonName');
        void confirm({
            title: 'Remove role member',
            message: `Remove ${name} from this role?`,
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `ApplicationRole/${roleId}/Person/${personId}`,
                    method: 'DELETE',
                    busy: `${rowSelector} .fa-spinner`,
                    onSuccess: () => remove(rowSelector),
                    onFailure: data => {
                        toast('danger', `Failed to remove ${name} from the role.`, summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to delete the role membership.', error);
                        return true;
                    },
                });
            },
        });
    },

    NewClaim(): void {
        const rowSelector = 'tr.new-role-claim-row';
        setHidden(rowSelector, false);
        void ajaxSwap({
            container: rowSelector,
            url: `ApplicationRole/${value('RoleId')}/Claim/Add`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to initialize the new role permission entry-form',
        });
    },

    SaveNewClaim(): void {
        remove('.role-claim-row-validation');
        void saveInlineRow('ApplicationRole/Claim', claimRowData(one('tr.new-role-claim-row')),
            'new-role-claim-row', 'Failed to save new role permission.');
    },

    CancelNewClaim(): void {
        setHidden('tr.new-role-claim-row', true);
    },

    EditClaim(claimId: number | string): void {
        const rowSelector = `tr.role-claim-row[data-id='${claimId}']`;
        setHidden(rowSelector, false);
        void ajaxSwap({
            container: rowSelector,
            url: `ApplicationRole/Claim/${claimId}/Edit`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            alertTitle: 'Failed to initialize the role permission entry-form',
        });
    },

    SaveEditClaim(claimId: number | string): void {
        const rowSelector = `tr.role-claim-row[data-id='${claimId}']`;
        const row = one(rowSelector);
        const data = claimRowData(row);
        data['id'] = valueIn(row, '#Claim_Id');
        void ajaxSwap({
            container: rowSelector,
            url: `ApplicationRole/Claim/${claimId}`,
            method: 'PUT',
            data,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            onSuccess: () => remove('.role-claim-row-validation'),
            onError: (xhr, _status, error) => {
                toast('danger', 'Failed to save the role permission.', xhr.status === 404 ? 'Not found' : error);
                return true;
            },
        });
    },

    CancelEditClaim(claimId: number | string): void {
        const rowSelector = `tr.role-claim-row[data-id='${claimId}']`;
        void ajaxSwap({
            container: rowSelector,
            url: `ApplicationRole/Claim/${claimId}`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            onSuccess: () => remove('.role-claim-row-validation'),
            onError: () => {
                toast('danger', 'Failed to restore the role permission row');
                return true;
            },
        });
    },

    RemoveClaim(claimId: number | string): void {
        const rowSelector = `tr.role-claim-row[data-id='${claimId}']`;
        const name = one(`${rowSelector} .resource-name`)?.textContent ?? '';
        void confirm({
            title: 'Remove role permission',
            message: `Remove permission for ${name} from this role?`,
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `ApplicationRole/Claim/${claimId}`,
                    method: 'DELETE',
                    busy: `${rowSelector} .fa-spinner`,
                    onSuccess: () => remove(rowSelector),
                    onFailure: data => {
                        toast('danger', 'Failed to remove the role permission.', summaryOf(data));
                        return true;
                    },
                    onError: (xhr, _status, error) => {
                        toast('danger', 'Failed to remove the role permission.', xhr.status === 404 ? 'Not found' : error);
                        return true;
                    },
                });
            },
        });
    },

    /**
     * The chosen ClaimValueType decides the value control: the server renders
     * the replacement, and the swap enhancers turn a data-autocomp control
     * into a datalist (the jQuery UI autocomplete wiring this used to carry
     * went with jQuery UI).
     */
    ClaimValueTypeChange(claimId: number | string): void {
        const rowSelector = String(claimId) === '0'
            ? 'tr.new-role-claim-row'
            : `tr.role-claim-row[data-id='${claimId}']`;
        const row = one(rowSelector);
        void ajaxSwap({
            container: `${rowSelector} #Claim_ClaimValue`,
            url: `ApplicationRole/Claim/ControlHtml?domainId=${encodeURIComponent(currentDomainId())}`,
            method: 'POST',
            data: {
                Id: claimId,
                RoleId: valueIn(row, '#RoleId'),
                ResourceId: valueIn(row, '#Claim_ResourceId'),
                ClaimValue: valueIn(row, '#Claim_ClaimValue'),
                ClaimValueType: valueIn(row, '#Claim_ClaimValueType'),
            },
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to update the value edit control', error);
                return true;
            },
            // Save is held off only while the value control is being replaced; it never
            // came back before, so a new row's Save (rendered disabled until a value type
            // is chosen) stayed dead.
            onComplete: () => {
                for (const node of all(`${rowSelector} .fa-save, ${rowSelector} .fa-edit, ${rowSelector} .fa-trash`)) node.removeAttribute('disabled');
            },
        });
        remove(`tr.role-claim-row-validation[data-id='${claimId}']`);
        for (const node of all(`${rowSelector} .fa-save`)) node.setAttribute('disabled', 'disabled');
    },
};

// ---------------------------------------------------------------- Person access

interface DupSentinel {
    action: string;
    existingId: number;
    existingValue: string;
    existingIsDeny: boolean;
}

interface ClaimAttempt extends Record<string, unknown> {
    PersonId: string;
    ResourceId: string;
    ClaimValue: string;
    IsDeny: boolean;
    ClaimValueType?: string;
    ResourcePolicyId?: string;
    Id?: string | number;
}

/**
 * The API attaches __dup* sentinel keys to a duplicate rejection. They are
 * handler hints, not user-facing, so they are stripped from modelState before
 * the summary is built (modelStateSummary also skips "__" keys; removing them
 * keeps the two rules from drifting apart).
 */
function extractDupSentinel(data: unknown): DupSentinel | null {
    const modelState = (data as { modelState?: Record<string, string[] | string> } | null)?.modelState;
    if (!modelState) return null;
    const pick = (key: string): string | null => {
        const raw = modelState[key];
        if (raw === undefined) return null;
        return Array.isArray(raw) ? (raw[0] ?? null) : raw;
    };
    const idText = pick('__dupExistingId');
    const action = pick('__dupAction');
    const existingValue = pick('__dupExistingValue');
    const existingIsDeny = pick('__dupExistingIsDeny');
    delete modelState['__dupExistingId'];
    delete modelState['__dupAction'];
    delete modelState['__dupExistingValue'];
    delete modelState['__dupExistingIsDeny'];
    if (!idText) return null;
    const id = Number.parseInt(idText, 10);
    if (!Number.isFinite(id)) return null;
    return {
        action: action ?? 'block',
        existingId: id,
        existingValue: existingValue ?? '',
        existingIsDeny: existingIsDeny === 'true' || existingIsDeny === 'True',
    };
}

function resolveModal(): HTMLElement {
    let node = document.getElementById('McpDupResolveModal');
    if (!node) {
        node = el('div', 'modal fade');
        node.id = 'McpDupResolveModal';
        node.tabIndex = -1;
        node.setAttribute('aria-hidden', 'true');
        node.innerHTML =
            '<div class="modal-dialog modal-dialog-centered modal-lg"><div class="modal-content">'
            + '<div class="modal-header"><h5 class="modal-title">'
            + '<i class="fas fa-exclamation-circle text-warning" aria-hidden="true"></i>'
            + '<span class="mcp-resolve-title"></span></h5>'
            + '<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button></div>'
            + '<div class="modal-body"></div>'
            + '<div class="modal-footer flex-wrap"></div>'
            + '</div></div>';
        document.body.appendChild(node);
    }
    return node;
}

export const Person = {
    NewPerson(appId: number | string): void {
        const rowSelector = 'tr.new-application-person-row';
        setHidden(rowSelector, false);
        void ajaxSwap({
            container: rowSelector,
            url: `ApplicationPerson/${appId}/Person/Add?domainId=${encodeURIComponent(currentDomainId())}`,
            mode: 'replaceWith',
            alertTitle: 'Failed to render new-person entry form',
        });
    },

    SaveNewPerson(appId: number | string): void {
        const row = one('tr.new-application-person-row');
        const personId = value('PersonId');
        void ajaxJson({
            url: `ApplicationPerson/${appId}/Person/${personId}`,
            method: 'POST',
            busy: 'tr.new-application-person-row .fa-spinner',
            onSuccess: () => {
                if (row) {
                    swapHtml(row, `<tr class="application-person-row" data-id="${personId}"><td colspan="4"><span class="fas fa-spinner fa-spin"></span></td></tr>`, 'before');
                    row.classList.add('d-none');
                }
                Person.PersonRow(personId);
            },
            onFailure: () => {
                toast('danger', 'Failed to add person to the application');
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to add person to the application', error);
                return true;
            },
        });
    },

    PersonRow(personId: number | string): void {
        const rowSelector = `tr.application-person-row[data-id='${personId}']`;
        void ajaxSwap({
            container: rowSelector,
            url: `ApplicationPerson/Person/${personId}`,
            mode: 'replaceWith',
            busy: `${rowSelector} .fa-spinner`,
            onError: () => {
                const row = one(rowSelector);
                if (row) row.innerHTML = '<td colspan="4"><span class="text-danger">Failed to render person row</span></td>';
                return true;
            },
        });
    },

    Remove(personId: number | string): void {
        const rowSelector = `.application-person-row[data-id='${personId}']`;
        const appId = applicationId();
        void confirm({
            title: 'Remove person access',
            message: "Remove this person's access to the application?",
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `ApplicationPerson/${appId}/Person/${personId}`,
                    method: 'DELETE',
                    busy: `${rowSelector} .fa-spinner`,
                    onSuccess: () => remove(rowSelector),
                    onFailure: data => {
                        toast('danger', 'Failed to remove person access', summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to remove person access', error);
                        return true;
                    },
                });
            },
        });
    },

    CancelNewPerson(): void {
        setHidden('.new-application-person-row', true);
    },

    AccessDetail(personId: number | string): void {
        const appId = applicationId();
        setDisplay(`RemovePersonButton${personId}`, false);
        void ajaxModal({
            modal: 'ApplicationDetailModal',
            url: `ApplicationPerson/${appId}/Person/${personId}`,
            errorTitle: 'Application Access',
            errorMessage: 'Failed to display permissions detail',
            busy: `#PersonBusyIndicator${personId}`,
            onComplete: () => {
                setDisplay(`RemovePersonButton${personId}`, true);
                Person.LoadEffective(appId, personId);
            },
        });
    },

    /**
     * Effective Permissions table, lazily loaded once the access modal is up.
     * The same response feeds the Test Access resource dropdown, so testing a
     * grant costs no extra round-trip.
     */
    LoadEffective(appId: number | string, personId: number | string): void {
        void ajaxSwap({
            container: 'PersonAccessEffective',
            url: `ApplicationPerson/${appId}/Person/${personId}/Effective`,
            busy: '#PersonAccessEffectiveBusy',
            onComplete: () => {
                setDisplay('PersonAccessEffectiveBusy', false);
                const select = document.getElementById('PersonAccessTestResource') as HTMLSelectElement | null;
                if (!select) return;
                select.replaceChildren();
                for (const row of all('#PersonAccessEffective tr.effective-permission-row')) {
                    select.add(new Option(row.getAttribute('data-resource-name') ?? '', row.getAttribute('data-resource-id') ?? ''));
                }
                select.disabled = false;
            },
            onError: (_xhr, status, error) => {
                const host = one('PersonAccessEffective');
                if (host) {
                    host.replaceChildren(el('div', 'text-danger small', `Failed to load effective permissions: ${error || status}`));
                }
                setDisplay('PersonAccessEffectiveBusy', false);
                return true;
            },
        });
    },

    /** Trace the (resource, operation) pair the Test form names. */
    TestAccess(): void {
        const appId = value('PersonAccessApplicationId');
        const personId = value('PersonAccessPersonId');
        const resourceId = value('PersonAccessTestResource');
        const operation = value('PersonAccessTestOperation');
        if (!resourceId) {
            toast('warning', 'Pick a resource');
            return;
        }
        void ajaxSwap({
            container: 'PersonAccessTraceResult',
            url: `ApplicationPerson/${appId}/Person/${personId}/Trace?resourceId=${encodeURIComponent(resourceId)}&operation=${encodeURIComponent(operation)}`,
            onError: (_xhr, status, error) => {
                const host = one('PersonAccessTraceResult');
                if (host) host.replaceChildren(el('div', 'text-danger small', `Trace failed: ${error || status}`));
                return true;
            },
        });
    },

    /** A cell in the Effective grid: pre-select it in the Test form and trace. */
    ShowEffectiveTrace(resourceId: number | string, operation: string): void {
        const resource = document.getElementById('PersonAccessTestResource') as HTMLSelectElement | null;
        const op = document.getElementById('PersonAccessTestOperation') as HTMLSelectElement | null;
        if (resource) resource.value = String(resourceId);
        if (op) op.value = operation;
        Person.TestAccess();
    },

    // ----- role membership -----

    RoleAddView(appId: number | string, personId: number | string): void {
        setDisplay('PersonRoleAddButton', false);
        setDisplay('PersonAccessRoleBusyIndicator', true);
        void ajaxSwap({
            container: 'PersonAccessRoleAddContainer',
            url: `ApplicationPerson/${appId}/Person/${personId}/RoleAdd/`,
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to display detail to add a role membership', error);
                setDisplay('PersonRoleAddButton', true);
                return true;
            },
            onComplete: () => setDisplay('PersonAccessRoleBusyIndicator', false),
        });
    },

    RoleAddSave(): void {
        const appId = value('PersonAccessApplicationId');
        const personId = value('PersonAccessPersonId');
        const roleId = value('ApplicationPersonRoleAddId');
        const saveButton = document.getElementById('PersonRoleSaveButton') as HTMLButtonElement | null;

        setDisplay('PersonAccessRoleBusyIndicator', true);
        if (saveButton) saveButton.disabled = true;
        void ajaxJson({
            url: `ApplicationPerson/${appId}/Person/${personId}/RoleAdd/${roleId}`,
            method: 'POST',
            onSuccess: () => {
                Person.AppendNewRole(roleId);
                one('PersonAccessRoleAddContainer')?.replaceChildren();
                Person.LoadEffective(appId, personId);
            },
            onFailure: data => {
                toast('danger', 'Failed to assign role membership', summaryOf(data));
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to assign role membership', error);
                return true;
            },
            onComplete: () => {
                setDisplay('PersonAccessRoleBusyIndicator', false);
                if (saveButton) saveButton.disabled = false;
                setDisplay('PersonRoleAddButton', true);
            },
        });
    },

    /**
     * Append the just-assigned role to the modal's membership list. The legacy
     * version wrote an onclick calling Application.Person.RemoveRole, a name
     * that has never existed -- the remove icon on a freshly added role did
     * nothing until the modal was reopened.
     */
    AppendNewRole(roleId: number | string): void {
        const list = one('PersonAccessRoles');
        if (!list) return;
        const select = document.getElementById('ApplicationPersonRoleAddId') as HTMLSelectElement | null;
        const roleName = select?.selectedOptions[0]?.textContent?.trim() || `New Role (ID:${roleId})`;

        const row = el('div', 'row person-role-row align-items-center');
        row.setAttribute('data-id', String(roleId));
        const label = el('div', 'col-10');
        label.appendChild(el('span', undefined, roleName));
        const actions = el('div', 'col-2 text-end');
        const button = el('button', 'btn btn-icon btn-icon-danger');
        button.type = 'button';
        button.title = 'Remove role membership';
        button.setAttribute('data-action', 'Application.Person.RoleRemove');
        button.setAttribute('data-args', `[${roleId}]`);
        button.innerHTML = '<i class="fas fa-trash"></i>';
        actions.appendChild(button);
        row.append(label, actions);
        list.appendChild(row);
    },

    /** The application/person pair lives in hidden fields; the view passes only the role. */
    RoleRemove(roleId: number | string): void {
        const appId = value('PersonAccessApplicationId');
        const personId = value('PersonAccessPersonId');
        void confirm({
            title: 'Remove role membership',
            message: 'Remove this role membership?',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                setDisplay(`RemoveRoleButton${roleId}`, false);
                setDisplay(`RoleBusyIndicator${roleId}`, true);
                void ajaxJson({
                    url: `ApplicationPerson/${appId}/Person/${personId}/RoleRemove/${roleId}`,
                    method: 'POST',
                    onSuccess: () => {
                        remove(`div.person-role-row[data-id='${roleId}']`);
                        toast('success', 'Removed role membership');
                        Person.LoadEffective(appId, personId);
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to remove role membership', summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to remove role membership', error);
                        return true;
                    },
                    onComplete: () => {
                        setDisplay(`RoleBusyIndicator${roleId}`, false);
                        setDisplay(`RemoveRoleButton${roleId}`, true);
                    },
                });
            },
        });
    },

    // ----- individual permissions -----

    ClaimAddView(appId: number | string, personId: number | string): void {
        setClaimEditingActive(true);
        setDisplay('PersonAccessClaimBusyIndicator', true);
        void ajaxSwap({
            container: 'PersonAccessClaimAddContainer',
            url: `ApplicationPerson/${appId}/Person/${personId}/ClaimAdd/`,
            onError: (_xhr, status, error) => {
                toast('danger', 'Failed to display detail to add a new permission', `${status} ${error}`);
                setClaimEditingActive(false);
                return true;
            },
            onComplete: () => setDisplay('PersonAccessClaimBusyIndicator', false),
        });
    },

    ClaimAddSave(): void {
        const appId = value('PersonAccessApplicationId');
        const personId = value('PersonAccessPersonId');
        const valueSelect = document.getElementById('ApplicationPersonClaimAddValue') as HTMLSelectElement | HTMLInputElement | null;
        const attempted = {
            resourceName: (document.getElementById('ApplicationPersonClaimAddResourceId') as HTMLSelectElement | null)?.selectedOptions[0]?.textContent ?? '',
            claimValue: (valueSelect as HTMLSelectElement | null)?.selectedOptions?.[0]?.textContent || valueSelect?.value || '',
            isDeny: (document.getElementById('ApplicationPersonClaimAddIsDeny') as HTMLInputElement | null)?.checked === true,
        };
        const formData: ClaimAttempt = {
            PersonId: personId,
            ResourceId: value('ApplicationPersonClaimAddResourceId'),
            ClaimValue: value('ApplicationPersonClaimAddValue'),
            IsDeny: attempted.isDeny,
            ResourcePolicyId: value('ApplicationPersonClaimAddResourcePolicyId'),
        };

        const saveButton = document.getElementById('PersonClaimSaveButton') as HTMLButtonElement | null;
        setDisplay('PersonAccessClaimBusyIndicator', true);
        if (saveButton) saveButton.disabled = true;
        void ajaxJson({
            url: `ApplicationPerson/${appId}/Person/${personId}/ClaimAdd`,
            method: 'POST',
            data: formData,
            onSuccess: () => {
                one('PersonAccessClaimAddContainer')?.replaceChildren();
                setClaimEditingActive(false);
                Person.RefreshClaims(appId, personId);
                toast('success', 'Permission granted');
            },
            onFailure: data => {
                const sentinel = extractDupSentinel(data);
                const summary = summaryOf(data);
                if (sentinel?.action === 'combine') {
                    offerCombine(appId, personId, sentinel, formData, summary);
                    return true;
                }
                if (sentinel?.action === 'oppositeDeny') {
                    offerOppositeIsDeny(appId, personId, sentinel, formData, summary);
                    return true;
                }
                toast('danger', 'Failed to grant permission', summary);
                if (sentinel?.existingId) {
                    Person.HighlightDuplicate(appId, personId, sentinel.existingId, attempted);
                }
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to grant permission', error);
                return true;
            },
            onComplete: () => {
                setDisplay('PersonAccessClaimBusyIndicator', false);
                if (saveButton) saveButton.disabled = false;
            },
        });
    },

    ClaimAddCancel(): void {
        one('PersonAccessClaimAddContainer')?.replaceChildren();
        setClaimEditingActive(false);
    },

    ClaimRemove(claimId: number | string): void {
        const appId = value('PersonAccessApplicationId');
        const personId = value('PersonAccessPersonId');
        void confirm({
            title: 'Remove permission',
            message: 'Remove this permission?',
            confirmText: 'Remove',
            variant: 'danger',
            onConfirm: () => {
                setDisplay(`RemoveClaimButton${claimId}`, false);
                setDisplay(`ClaimBusyIndicator${claimId}`, true);
                void ajaxJson({
                    url: `ApplicationPerson/${appId}/Person/${personId}/ClaimRemove/${claimId}`,
                    method: 'POST',
                    onSuccess: () => {
                        Person.RefreshClaims(appId, personId);
                        toast('success', 'Removed permission');
                    },
                    onFailure: data => {
                        toast('danger', 'Failed to remove permission', summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, _status, error) => {
                        toast('danger', 'Failed to remove permission', error);
                        return true;
                    },
                    onComplete: () => {
                        setDisplay(`ClaimBusyIndicator${claimId}`, false);
                        setDisplay(`RemoveClaimButton${claimId}`, true);
                    },
                });
            },
        });
    },

    /** Swap one read-only permission row for its inline edit form. */
    ClaimEditView(claimId: number | string): void {
        const appId = value('PersonAccessApplicationId');
        const personId = value('PersonAccessPersonId');
        setClaimEditingActive(true);
        void ajaxSwap({
            container: `div.person-claim-row[data-id='${claimId}']`,
            url: `ApplicationPerson/${appId}/Person/${personId}/Claim/${claimId}/Edit`,
            mode: 'replaceWith',
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to load edit form', error);
                setClaimEditingActive(false);
                return true;
            },
        });
    },

    ClaimEditSave(claimId: number | string): void {
        const appId = value('PersonAccessApplicationId');
        const personId = value('PersonAccessPersonId');
        const form: ClaimAttempt = {
            Id: claimId,
            PersonId: personId,
            ResourceId: value('PersonClaimEditResourceId'),
            ClaimValue: value('PersonClaimEditValue'),
            ClaimValueType: value('PersonClaimEditValueType'),
            IsDeny: (document.getElementById('PersonClaimEditIsDeny') as HTMLInputElement | null)?.checked === true,
            // Present-but-empty clears the window; the dup-resolution PUTs
            // below deliberately OMIT this key so a value fix leaves the
            // window untouched.
            ResourcePolicyId: value('PersonClaimEditResourcePolicyId'),
        };
        void ajaxJson({
            url: 'PersonClaim/Resource',
            method: 'PUT',
            data: form,
            onSuccess: () => {
                setClaimEditingActive(false);
                Person.RefreshClaims(appId, personId);
                toast('success', 'Permission updated');
            },
            onFailure: data => {
                const sentinel = extractDupSentinel(data);
                const summary = summaryOf(data);
                if (sentinel?.action === 'deleteSelf') {
                    offerDeleteSelf(appId, personId, claimId, sentinel, summary);
                    return true;
                }
                if (sentinel?.action === 'combine') {
                    offerCombine(appId, personId, sentinel, form, summary);
                    return true;
                }
                if (sentinel?.action === 'oppositeDeny') {
                    offerOppositeIsDeny(appId, personId, sentinel, form, summary);
                    return true;
                }
                toast('danger', 'Failed to save permission', summary);
                return true;
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to save permission', error);
                return true;
            },
        });
    },

    ClaimEditCancel(): void {
        const appId = value('PersonAccessApplicationId');
        const personId = value('PersonAccessPersonId');
        setClaimEditingActive(false);
        Person.RefreshClaims(appId, personId);
    },

    /**
     * Duplicate feedback: refresh the list, lift the existing row to the top
     * and put a synthetic "attempted" ghost above it so the two values can be
     * compared. Both highlights fade after eight seconds.
     */
    HighlightDuplicate(appId: number | string, personId: number | string, existingId: number, attempted: { resourceName: string; claimValue: string; isDeny: boolean }): void {
        void ajaxSwap({
            container: 'PersonAccessClaims',
            url: `ApplicationPerson/${appId}/Person/${personId}/Claims`,
            onSuccess: () => {
                const list = one('PersonAccessClaimList');
                const existing = list?.querySelector(`div.person-claim-row[data-id="${existingId}"]`);
                if (!list || !existing) return;
                list.prepend(existing);
                existing.classList.add('person-claim-conflict-existing');

                const ghost = el('div', 'row person-claim-row person-claim-conflict-attempted align-items-center');
                ghost.setAttribute('data-id', '0');
                const target = el('div', 'col-5');
                target.appendChild(el('span', 'badge bg-warning text-dark me-1', 'ATTEMPTED'));
                target.appendChild(el('span', undefined, attempted.resourceName));
                const claimValue = el('div', 'col-4', attempted.claimValue);
                const deny = el('div', 'col-1');
                if (attempted.isDeny) {
                    const badge = el('span', 'badge bg-danger', 'DENY');
                    badge.title = 'Explicit deny -- wins over every grant';
                    deny.appendChild(badge);
                }
                ghost.append(target, claimValue, deny, el('div', 'col-2'));
                list.prepend(ghost);

                window.setTimeout(() => {
                    ghost.remove();
                    existing.classList.remove('person-claim-conflict-existing');
                }, 8000);
            },
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to refresh permission list', error);
                return true;
            },
        });
    },

    /**
     * Re-fetch the modal's permission list after any change, and the Effective
     * grid with it -- a new grant lights cells and a deny darkens them, so a
     * stale grid is misleading.
     */
    RefreshClaims(appId: number | string, personId: number | string): void {
        void ajaxSwap({
            container: 'PersonAccessClaims',
            url: `ApplicationPerson/${appId}/Person/${personId}/Claims`,
            onError: (_xhr, _status, error) => {
                toast('danger', 'Failed to refresh permission list', error);
                return true;
            },
        });
        Person.LoadEffective(appId, personId);
    },
};

/**
 * While an add/edit is in flight the Individual Permissions section hides its
 * (+) button and fades the read-only rows, so the only way out is finish or
 * cancel.
 */
function setClaimEditingActive(active: boolean): void {
    one('PersonAccessClaims')?.classList.toggle('editing-active', active);
    setDisplay('PersonClaimAddButton', !active);
}

// ----- duplicate-claim resolutions

interface ReplaceOptions {
    appId: number | string;
    personId: number | string;
    deleteIds: number[];
    newClaim: Record<string, unknown>;
    successMsg: string;
    failTitle: string;
}

function afterResolution(appId: number | string, personId: number | string, message: string): void {
    one('PersonAccessClaimAddContainer')?.replaceChildren();
    setClaimEditingActive(false);
    Person.RefreshClaims(appId, personId);
    toast('success', message);
}

/** Atomic "delete N + create 1": the server wraps both sides in one SaveChanges. */
function postReplace(options: ReplaceOptions): void {
    void ajaxJson({
        url: 'PersonClaim/Replace',
        method: 'POST',
        contentType: 'application/json',
        data: { DeleteIds: options.deleteIds, NewClaim: options.newClaim },
        onSuccess: () => afterResolution(options.appId, options.personId, options.successMsg),
        onFailure: data => {
            toast('danger', options.failTitle, summaryOf(data));
            return true;
        },
        onError: (_xhr, status, error) => {
            toast('danger', options.failTitle, error || status);
            return true;
        },
    });
}

/** Atomic "update existing + optionally add a sibling"; same rollback guarantee. */
function postMerge(appId: number | string, personId: number | string, updateExisting: Record<string, unknown>, newClaim: Record<string, unknown> | null, successMsg: string, failTitle: string): void {
    void ajaxJson({
        url: 'PersonClaim/Merge',
        method: 'POST',
        contentType: 'application/json',
        data: { UpdateExisting: updateExisting, NewClaim: newClaim },
        onSuccess: () => afterResolution(appId, personId, successMsg),
        onFailure: data => {
            toast('danger', failTitle, summaryOf(data));
            return true;
        },
        onError: (_xhr, status, error) => {
            toast('danger', failTitle, error || status);
            return true;
        },
    });
}

/**
 * Same (target, IsDeny), different verb sets: offer one row carrying the
 * union instead of two rows that have to be read together.
 *
 * PUT context (formData.Id > 0 -- the operator was editing a row whose new
 * state collides with another): the editing row joins DeleteIds so it is
 * removed in the same transaction, or the merge silently leaves it behind at
 * its original state. POST context: only the existing target row is deleted,
 * because the attempt was never persisted.
 */
function offerCombine(appId: number | string, personId: number | string, sentinel: DupSentinel, formData: ClaimAttempt, summary: string): void {
    const newRaw = String(formData.ClaimValue ?? '');
    const unionRaw = unionVerbs(sentinel.existingValue, newRaw);
    const newDisplay = friendlyVerbList(newRaw);
    const oldDisplay = friendlyVerbList(sentinel.existingValue);
    const unionDisplay = friendlyVerbList(unionRaw);

    const editingId = Number.parseInt(String(formData.Id ?? ''), 10);
    const deleteIds = [sentinel.existingId];
    if (Number.isFinite(editingId) && editingId > 0 && editingId !== sentinel.existingId) deleteIds.push(editingId);

    // Built as DOM so the operator-supplied values are escaped, then handed
    // over as markup (confirm's html option takes a string).
    const body = el('div');
    const first = el('p');
    first.append(`An existing claim (id #${sentinel.existingId}) already exists on this target with value `);
    first.appendChild(el('code', undefined, oldDisplay));
    first.append('.');
    const second = el('p');
    second.append('You attempted to add ');
    second.appendChild(el('code', undefined, newDisplay));
    second.append('. Combining merges them into one row with value ');
    second.appendChild(el('code', undefined, unionDisplay));
    second.append('.');
    body.append(first, second, el('p', 'text-body-secondary small mb-0',
        'If this is not what you intended, cancel and either edit the existing claim directly or pick a different claim type.'));

    void confirm({
        title: 'Combine into existing claim',
        variant: 'warning',
        confirmText: `Yes, combine into "${unionDisplay}"`,
        cancelText: 'No, reject save',
        html: body.innerHTML,
        onConfirm: () => postReplace({
            appId,
            personId,
            deleteIds,
            newClaim: {
                PersonId: personId,
                ResourceId: formData.ResourceId,
                ClaimValue: unionRaw,
                ClaimValueType: formData.ClaimValueType || RESTACTION,
                IsDeny: formData.IsDeny,
            },
            successMsg: `Combined into "${unionDisplay}"`,
            failTitle: 'Combine failed (rolled back)',
        }),
        onCancel: () => toast('danger', 'Save rejected', summary),
    });
}

/**
 * An existing claim with the OPPOSITE IsDeny shares verbs with the attempt.
 * Up to three resolutions:
 *   1. Flip -- one row with the opposite IsDeny carrying the union. Done as
 *      delete+create so the audit reads Revoke + Grant, not a polarity change
 *      on an existing id.
 *   2. Remove overlap (new-is-deny only, non-empty remainder) -- subtract the
 *      attempted verbs from the existing grant AND add the deny in full, in
 *      one transaction. The deny keeps its complete value on purpose: an
 *      explicit deny is defensive and overrides future grants from roles or
 *      manifest seeds, so the operator's stated intent is preserved verbatim.
 *   3. Overwrite (new-is-deny only) -- replace the existing row's value and
 *      polarity outright.
 * The empty-subtract case is deliberately not offered: Flip and Overwrite
 * already cover it and a default would silently drop the new deny.
 */
function offerOppositeIsDeny(appId: number | string, personId: number | string, sentinel: DupSentinel, formData: ClaimAttempt, summary: string): void {
    const newIsDeny = formData.IsDeny === true;
    const newValue = String(formData.ClaimValue ?? '');
    const newDisplay = friendlyVerbList(newValue);
    const existingDisplay = friendlyVerbList(sentinel.existingValue);
    const valueType = formData.ClaimValueType || RESTACTION;

    const node = resolveModal();
    const title = node.querySelector('.mcp-resolve-title');
    if (title) title.textContent = `Conflicting ${sentinel.existingIsDeny ? 'deny' : 'grant'} on the same target`;

    const body = node.querySelector('.modal-body');
    if (body) {
        body.replaceChildren(
            el('p', undefined,
                `An existing ${sentinel.existingIsDeny ? 'deny' : 'grant'} (claim #${sentinel.existingId}, `
                + `value "${existingDisplay}") shares verbs with your new ${newIsDeny ? 'deny' : 'grant'} `
                + `(value "${newDisplay}").`),
            el('p', 'text-body-secondary small mb-0', 'Pick how you want to resolve the overlap. "Cancel" rejects the save.'));
    }

    const footer = node.querySelector('.modal-footer');
    if (!footer) return;
    footer.replaceChildren();

    /** Run the resolution after the dialog has closed, so its toast is not covered. */
    const hideThen = (action: () => void) => (): void => {
        node.addEventListener('hidden.bs.modal', action, { once: true });
        const Modal = window.bootstrap?.Modal;
        if (Modal) Modal.getOrCreateInstance(node).hide();
        else { node.classList.remove('show', 'd-block'); action(); }
    };
    const addButton = (className: string, label: string, action: () => void): void => {
        const button = el('button', `btn ${className}`, label);
        button.type = 'button';
        button.addEventListener('click', hideThen(action));
        footer.appendChild(button);
    };

    // 1. Flip.
    const flipUnionRaw = unionVerbs(sentinel.existingValue, newValue);
    const flipUnionDisplay = friendlyVerbList(flipUnionRaw);
    const flipTargetIsDeny = !sentinel.existingIsDeny;
    const editingId = Number.parseInt(String(formData.Id ?? ''), 10);
    const flipDeleteIds = [sentinel.existingId];
    if (Number.isFinite(editingId) && editingId > 0 && editingId !== sentinel.existingId) flipDeleteIds.push(editingId);
    addButton('btn-warning', `Flip -> combined ${flipTargetIsDeny ? 'deny' : 'allow'} "${flipUnionDisplay}"`, () => postReplace({
        appId,
        personId,
        deleteIds: flipDeleteIds,
        newClaim: {
            PersonId: personId,
            ResourceId: formData.ResourceId,
            ClaimValue: flipUnionRaw,
            ClaimValueType: valueType,
            IsDeny: flipTargetIsDeny,
        },
        successMsg: `Combined into "${flipUnionDisplay}" ${flipTargetIsDeny ? 'deny' : 'allow'}`,
        failTitle: 'Replace failed (rolled back)',
    }));

    if (newIsDeny) {
        // 2. Remove overlap.
        const subtracted = subtractVerbs(sentinel.existingValue, newValue);
        if (subtracted) {
            const subtractedDisplay = friendlyVerbList(subtracted);
            addButton('btn-outline-primary',
                `Remove overlap from existing -> "${subtractedDisplay}" + add deny "${newDisplay}"`,
                () => postMerge(appId, personId, {
                    Id: sentinel.existingId,
                    PersonId: personId,
                    ResourceId: formData.ResourceId,
                    ClaimType: (formData as Record<string, unknown>)['ClaimType'],
                    ClaimValue: subtracted,
                    ClaimValueType: valueType,
                    IsDeny: sentinel.existingIsDeny,
                }, {
                    PersonId: personId,
                    ResourceId: formData.ResourceId,
                    ClaimType: (formData as Record<string, unknown>)['ClaimType'],
                    ClaimValue: newValue,
                    ClaimValueType: valueType,
                    IsDeny: true,
                }, `Overlap removed; deny "${newDisplay}" added`, 'Merge failed (rolled back)'));
        }

        // 3. Overwrite.
        addButton('btn-outline-danger', `Overwrite existing with "${newDisplay}" deny`, () => {
            void ajaxJson({
                url: 'PersonClaim/Resource',
                method: 'PUT',
                data: {
                    Id: sentinel.existingId,
                    PersonId: personId,
                    ResourceId: formData.ResourceId,
                    ClaimValue: newValue,
                    ClaimValueType: valueType,
                    IsDeny: true,
                },
                onSuccess: () => afterResolution(appId, personId, 'Existing claim overwritten'),
                onFailure: data => {
                    toast('danger', 'Update failed', summaryOf(data));
                    return true;
                },
                onError: (_xhr, _status, error) => {
                    toast('danger', 'Update failed', error);
                    return true;
                },
            });
        });
    }

    addButton('btn-outline-secondary', 'Cancel -- reject save', () => toast('danger', 'Save rejected', summary));
    showModalElement(node);
}

/** The edit would duplicate another row: offer to delete the row being edited. */
function offerDeleteSelf(appId: number | string, personId: number | string, claimId: number | string, sentinel: DupSentinel, summary: string): void {
    void confirm({
        title: 'This change would duplicate an existing claim',
        variant: 'danger',
        confirmText: 'Yes, delete this row',
        cancelText: 'No, reject save',
        message: `Saving would make this row a duplicate of claim #${sentinel.existingId} `
            + `(value "${friendlyVerbList(sentinel.existingValue)}"). Delete the row you are editing instead?`,
        onConfirm: () => {
            void ajaxJson({
                url: `ApplicationPerson/${appId}/Person/${personId}/ClaimRemove/${claimId}`,
                method: 'POST',
                onSuccess: () => {
                    setClaimEditingActive(false);
                    Person.RefreshClaims(appId, personId);
                    toast('success', 'Row deleted');
                },
                onFailure: data => {
                    toast('danger', 'Delete failed', summaryOf(data));
                    return true;
                },
                onError: (_xhr, _status, error) => {
                    toast('danger', 'Delete failed', error);
                    return true;
                },
            });
        },
        onCancel: () => toast('danger', 'Save rejected', summary),
    });
}

// ---------------------------------------------------------------- Profile

/**
 * One Edit -> Save / Reset / Cancel toolbar commits every staged change on
 * the Profile tab. Each editable input (.ta-input in the Theme panel,
 * .cs-row-value in the Settings grid) carries the baseline it is compared
 * against, so Save can decide upsert-vs-delete per key. Deleting an ad-hoc
 * custom row is the only immediate action -- it is structural, not a value
 * edit.
 */
function profileContent(): HTMLElement | null {
    return one('.app-profile-content') as HTMLElement | null;
}

function themePanel(): HTMLElement | null {
    return one('.theme-app-panel') as HTMLElement | null;
}

function profileApplicationId(): string {
    return themePanel()?.dataset['applicationId'] ?? value('ApplicationProfile_ApplicationId');
}

function jsonData(node: HTMLElement | null, attribute: string): Record<string, Record<string, string>> {
    const raw = node?.getAttribute(attribute);
    if (!raw) return {};
    try {
        return JSON.parse(raw) as Record<string, Record<string, string>>;
    } catch {
        return {};
    }
}

/** One theme/system override input's staged value, honouring the bool shape. */
export function taValue(input: HTMLInputElement | HTMLSelectElement): string {
    if (input.getAttribute('data-ta-bool') === 'true' && input instanceof HTMLInputElement && input.type === 'checkbox') {
        // Round-trips as "true"/"" so an unchecked box still deletes the
        // override when it matches the template default. .value on a checkbox
        // is the value attribute regardless of checked state.
        return input.checked ? 'true' : '';
    }
    // An unset colour swatch still reports #000000; it stays "" until a
    // colour is actually picked (drag, palette or template preview).
    if (input.getAttribute('data-ta-unset') === 'true') return '';
    return input.value || '';
}

function taInputs(): (HTMLInputElement | HTMLSelectElement)[] {
    return all('.theme-app-panel .ta-input') as (HTMLInputElement | HTMLSelectElement)[];
}

/** POST a single setting; resolves to null on success or a message on failure. */
async function postSetting(appId: string, url: string, data: Record<string, unknown>, failMessage: string): Promise<string | null> {
    let message: string | null = failMessage;
    await ajaxJson({
        url,
        method: 'POST',
        data,
        onSuccess: resp => { message = resp['success'] === false ? (String(resp['message'] ?? failMessage)) : null; },
        onFailure: data => {
            message = String((data as { message?: string } | null)?.message || summaryOf(data) || failMessage);
            return true;
        },
        onError: (_xhr, status, error) => {
            message = error || status || failMessage;
            return true;
        },
    });
    return message;
}

export const Profile = {
    /** Reveal the inline new-row form in the Custom Settings grid. */
    AddClaim(): void {
        setHidden('.custom-claim-new-row', false);
        setHidden('.custom-claim-empty-row', true);
        const key = document.getElementById('CustomClaimNewKey') as HTMLInputElement | null;
        const newValue = document.getElementById('CustomClaimNewValue') as HTMLInputElement | null;
        if (key) { key.value = ''; key.focus(); }
        if (newValue) newValue.value = '';
    },

    CancelNewClaim(): void {
        setHidden('.custom-claim-new-row', true);
        const key = document.getElementById('CustomClaimNewKey') as HTMLInputElement | null;
        const newValue = document.getElementById('CustomClaimNewValue') as HTMLInputElement | null;
        if (key) key.value = '';
        if (newValue) newValue.value = '';
        if (all('#CustomClaimsTable .custom-claim-row').length === 0) {
            setHidden('.custom-claim-empty-row', false);
        }
    },

    /**
     * Delete an ad-hoc custom setting. Template-sourced rows cannot be
     * deleted (the template defines them); clearing the value and saving
     * drops the per-app override instead.
     */
    DeleteRow(this: Element): void {
        const row = this.closest('.custom-claim-row');
        const appId = value('ApplicationProfile_ApplicationId');
        const key = row?.getAttribute('data-key') ?? '';
        void confirm({
            title: 'Delete custom setting',
            message: `Delete setting '${key}'?`,
            confirmText: 'Delete',
            variant: 'danger',
            onConfirm: () => {
                void ajaxJson({
                    url: `Application/${appId}/Setting/Delete`,
                    method: 'POST',
                    data: { key },
                    onSuccess: resp => {
                        if (resp['success'] === false) {
                            toast('danger', String(resp['message'] ?? 'Failed to delete setting'));
                            return;
                        }
                        toast('success', 'Setting deleted.');
                        Profile.RefreshTab(appId);
                    },
                    onFailure: () => { toast('danger', 'Failed to delete setting'); return true; },
                    onError: (_xhr, _status, error) => { toast('danger', 'Failed to delete setting', error); return true; },
                });
            },
        });
    },

    RefreshTab(appId: number | string): void {
        void ajaxSwap({
            container: '#ApplicationProfileTab',
            url: `Application/${appId}/Profile`,
            alertTitle: 'Failed to refresh Profile tab',
        });
    },

    /**
     * Commit every staged change, in series so a partial failure cannot leave
     * an inconsistent mix: template binding, then theme + system overrides in
     * one round-trip, then the dirty custom settings, then the new ad-hoc row.
     */
    SaveAll(): void {
        void (async (): Promise<void> => {
            const content = profileContent();
            const panel = themePanel();
            const appId = profileApplicationId();
            const busy = one('ApplicationProfileBusyIndicator');
            busy?.classList.remove('d-none');
            const fail = (message: string, detail?: string): void => {
                busy?.classList.add('d-none');
                toast('danger', message, detail);
            };

            // 1. Pending template binding.
            const pendingTemplateId = content?.getAttribute('data-pending-templateid');
            if (pendingTemplateId !== null && pendingTemplateId !== undefined) {
                const error = await postSetting(appId, `Application/${appId}/SetTemplate`,
                    { templateId: pendingTemplateId }, 'Failed to set template');
                if (error) { fail(error); return; }
            }

            // 2. Theme + system overrides, one round-trip for the whole panel.
            const values: Record<string, string> = {};
            const defaults: Record<string, string> = {};
            for (const input of taInputs()) {
                const key = input.getAttribute('data-ta-key');
                if (!key) continue;
                values[key] = taValue(input);
                defaults[key] = input.getAttribute('data-ta-default') ?? '';
            }
            if (panel) {
                let themeError: string | null = null;
                await ajaxJson({
                    url: `Application/${appId}/ThemeOverrides`,
                    method: 'POST',
                    contentType: 'application/json',
                    data: { values, defaults },
                    onSuccess: resp => { if (resp['success'] === false) themeError = String(resp['message'] ?? 'Failed to save theme / system overrides'); },
                    onFailure: data => { themeError = summaryOf(data) || 'Failed to save theme / system overrides'; return true; },
                    onError: (_xhr, status, error) => { themeError = error || status; return true; },
                });
                if (themeError) { fail('Failed to save theme / system overrides', themeError); return; }
            }

            // 3. Dirty custom settings, one call per key.
            for (const row of all('#CustomClaimsTable .custom-claim-row')) {
                const field = row.querySelector('.cs-row-value') as HTMLInputElement | HTMLTextAreaElement | null;
                if (!field) continue;
                const current = field.value ?? '';
                const original = field.getAttribute('data-original-value') ?? '';
                // Trim for the comparison only: a padded value the operator
                // typed on purpose is submitted as typed.
                if (current.trim() === original.trim()) continue;
                const key = row.getAttribute('data-key') ?? '';
                const fromTemplate = row.getAttribute('data-from-template') === '1';
                const templateDefault = row.getAttribute('data-template-default') ?? '';
                const shouldDelete = fromTemplate
                    ? current.trim() === templateDefault.trim()
                    : current.trim().length === 0;
                const error = await postSetting(appId,
                    shouldDelete ? `Application/${appId}/Setting/Delete` : `Application/${appId}/Setting`,
                    shouldDelete ? { key } : { key, value: current },
                    `Failed to save '${key}'`);
                if (error) { fail(error); return; }
            }

            // 4. The new ad-hoc row, when it is visible and carries a key.
            const newRow = one('.custom-claim-new-row');
            if (newRow && !newRow.classList.contains('d-none')) {
                const newKey = value('CustomClaimNewKey').trim();
                if (newKey.length > 0) {
                    const error = await postSetting(appId, `Application/${appId}/Setting`,
                        { key: newKey, value: value('CustomClaimNewValue') }, 'Failed to add new setting');
                    if (error) { fail(error); return; }
                }
            }

            busy?.classList.add('d-none');
            toast('success', 'Profile saved');
            Profile.RefreshTab(appId);
        })();
    },

    /**
     * Drop every well-known override (theme + system) so the next Profile GET
     * shows the bound template's values again. Ad-hoc custom settings are
     * operator-added, not overrides, and are left alone.
     */
    ResetAll(): void {
        const appId = profileApplicationId();
        void confirm({
            title: 'Reset overrides',
            message: 'Discard every per-app theme & system override and fall back to the bound template values? '
                + 'Ad-hoc custom settings are not affected. This cannot be undone.',
            confirmText: 'Reset',
            variant: 'danger',
            onConfirm: () => {
                const busy = one('ApplicationProfileBusyIndicator');
                busy?.classList.remove('d-none');
                void ajaxJson({
                    url: `Application/${appId}/ThemeOverrides/Reset`,
                    method: 'POST',
                    onSuccess: () => {
                        toast('success', 'Overrides reset to template defaults');
                        Profile.RefreshTab(appId);
                    },
                    onFailure: data => {
                        toast('danger', 'Reset failed', summaryOf(data));
                        return true;
                    },
                    onError: (_xhr, status, error) => {
                        toast('danger', 'Reset failed', error || status);
                        return true;
                    },
                });
            },
        });
    },

    EnterEditMode(): void {
        profileContent()?.classList.add('app-profile-edit');
    },

    /** Leave edit mode without saving; the re-fetched partial restores the baselines. */
    CancelEdit(): void {
        Profile.RefreshTab(value('ApplicationProfile_ApplicationId'));
    },

    /**
     * STAGE a ClaimTemplate change (no commit). Re-previews the Theme panel
     * with the new template's defaults and updates each input's
     * data-ta-default so Save compares against the right baseline.
     */
    SelectTemplate(this: Element): void {
        const content = profileContent();
        if (!content) return;
        const newValue = String((this as HTMLSelectElement).value ?? '');
        const originalValue = content.getAttribute('data-original-templateid') ?? '0';
        const newDefaults = jsonData(content, 'data-template-themes')[newValue] ?? {};

        for (const input of taInputs()) {
            const key = input.getAttribute('data-ta-key') ?? '';
            const preset = newDefaults[key] ?? '';
            input.setAttribute('data-ta-default', preset);
            if (input.getAttribute('type') === 'color') {
                const isHex = preset.length === 7 && preset.startsWith('#');
                input.value = isHex ? preset : '#000000';
                input.setAttribute('data-ta-unset', isHex ? 'false' : 'true');
                const hex = input.closest('.row')?.querySelector('.ta-hex');
                if (hex) hex.textContent = preset || '(unset)';
            } else if (input.getAttribute('data-ta-bool') === 'true' && input instanceof HTMLInputElement && input.type === 'checkbox') {
                input.checked = preset === 'true';
            } else {
                input.value = preset;
            }
        }

        const pending = newValue !== originalValue;
        one('ApplicationTemplatePendingHint')?.classList.toggle('d-none', !pending);
        if (pending) content.setAttribute('data-pending-templateid', newValue);
        else content.removeAttribute('data-pending-templateid');
    },

    /** Stage a palette preset onto the Theme panel's colour inputs (no commit). */
    ApplyAppPreset(): void {
        const content = profileContent();
        const name = value('ApplicationThemePresetSelect');
        if (!content || !name) return;
        const palette = jsonData(content, 'data-palette-presets')[name];
        if (!palette) {
            toast('danger', 'Unknown palette preset', name);
            return;
        }
        let matched = 0;
        for (const input of taInputs()) {
            const key = input.getAttribute('data-ta-key') ?? '';
            if (!key || !Object.prototype.hasOwnProperty.call(palette, key)) continue;
            const hex = palette[key] ?? '';
            input.value = hex;
            input.setAttribute('data-ta-unset', 'false');
            const label = input.closest('.row')?.querySelector('.ta-hex');
            if (label) label.textContent = hex;
            matched++;
        }
        toast('success', `${name} palette applied`, `${matched} colour field(s) staged. Save to persist.`);
    },
};

/**
 * A colour picker's hex label follows the swatch as it is dragged. Not a
 * data-action: dispatch fires change on selects and toggles, and this needs
 * the live `input` stream to track the drag.
 */
function bindColourPreview(): void {
    document.addEventListener('input', event => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || !target.matches('.theme-app-panel input[type="color"].ta-input')) return;
        target.setAttribute('data-ta-unset', 'false');
        const label = target.closest('.row')?.querySelector('.ta-hex');
        if (label) label.textContent = target.value;
    });
}

/** Install the area's delegated listeners; called once from main.ts. */
export function initApplicationArea(): void {
    bindUrlPreview();
    bindResourceUriPreview();
    bindColourPreview();
}
